import { memo } from 'react'

import type { ToastRenderProps, ToastIntent, ToastSize, ToastStatus } from './toast.types'

import { styled } from '../core'
import { DismissButton } from '../dismissButton'
import { Icon } from '../icon'
import { Spinner } from '../spinner'
import { intentStyle } from './theme'

const intentIcon: Record<ToastIntent, string> = {
  info: 'uiInfoCircle',
  success: 'uiCheckCircle',
  warning: 'uiExclamationTriangle',
  danger: 'uiExclamationCircle',
}

const TOAST_ICON_SIZE = 16

// Typography tokens (caption with global token fallback)
const toastTypography = `
  --vui-toast-text-size: var(--vui-font-size-sm, 14px);
  --vui-toast-text-weight: var(--vui-caption-font-weight, 400);
  --vui-toast-text-line-height: var(--vui-caption-line-height, 150%);
  font-size: var(--vui-toast-text-size);
  font-weight: var(--vui-toast-text-weight);
  line-height: var(--vui-toast-text-line-height);
`

// Title typography (bold override)
const toastTitleTypography = `
  --vui-toast-title-weight: var(--vui-caption-font-weight-bold, 600);
  font-weight: var(--vui-toast-title-weight);
`

// Spacing tokens
const toastSpacing = `
  --vui-toast-gap-row: 16px;
  --vui-toast-gap-text: 0px;
  --vui-toast-gap-action-bar: 8px;
  --vui-toast-padding-row: 8px 16px;
  --vui-toast-padding-icon: 8px 0 0;
  --vui-toast-padding-text: 4px 0;
  --vui-toast-padding-action-bar: 8px 16px;
`

// Sizing tokens
const toastSizing = `
  --vui-toast-min-height: 56px;
  --vui-toast-min-width: 320px;
  --vui-toast-width-default: 480px;
  --vui-toast-width-wide: 560px;
  --vui-toast-text-clamp: 2;
  --vui-toast-title-line-height: 24px;
`

// Shadow tokens
const toastShadow = `
  --vui-toast-shadow:
    0px 0px 1px 0px rgba(0, 0, 0, 0.05),
    0px 10px 20px 0px rgba(0, 0, 0, 0.15);
`

// Animation tokens
const toastAnimation = `
  --vui-toast-animation-duration: 0.2s;
  --vui-toast-animation-timing: ease-in-out;
`

// All tokens combined — declared once on ToastRoot, cascade to all children
const toastTokens = `
  ${toastSpacing}
  ${toastSizing}
  ${toastShadow}
  ${toastAnimation}
`

const ToastRoot = styled.div<{ $status: ToastStatus; $size: ToastSize }>`
  ${toastTokens}
  animation: vui-fadeDown var(--vui-toast-animation-duration) var(--vui-toast-animation-timing);
  box-shadow: var(--vui-toast-shadow);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  min-height: var(--vui-toast-min-height);
  min-width: var(--vui-toast-min-width);
  width: ${({ $size }) => ($size === 'wide' ? 'var(--vui-toast-width-wide)' : 'var(--vui-toast-width-default)')};
  ${({ $status }) => intentStyle[$status]}
`

const Row = styled.div`
  align-items: stretch;
  display: flex;
  flex-direction: row;
  gap: var(--vui-toast-gap-row);
  padding: var(--vui-toast-padding-row);
`

const IconWrap = styled.div<{ $inline?: boolean }>`
  align-self: ${({ $inline }) => ($inline ? 'center' : 'flex-start')};
  color: var(--vui-toast-intent-color);
  display: flex;
  flex-shrink: 0;
  padding: ${({ $inline }) => ($inline ? '0' : 'var(--vui-toast-padding-icon)')};
`
const TextArea = styled.div<{ $inline?: boolean }>`
  ${toastTypography}
  display: ${({ $inline }) => ($inline ? 'block' : 'flex')};
  flex: 1;
  flex-direction: column;
  gap: var(--vui-toast-gap-text);
  min-width: 0;
  padding: var(--vui-toast-padding-text);
`

const InlineClamp = styled.span`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: var(--vui-toast-text-clamp);
  color: var(--vui-toast-intent-color);
  display: -webkit-box;
  overflow: hidden;
  word-break: break-word;
`

const Title = styled.p<{ $inline?: boolean }>`
  ${toastTitleTypography}
  color: var(--vui-toast-intent-color);
  display: ${({ $inline }) => ($inline ? 'inline' : 'block')};
  line-height: var(--vui-toast-title-line-height);
  margin: 0;
`

const Description = styled.p<{ $inline?: boolean }>`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: var(--vui-toast-text-clamp);
  color: var(--vui-toast-intent-color);
  display: ${({ $inline }) => ($inline ? 'inline' : '-webkit-box')};
  margin: 0;
  overflow: hidden;
`

const DismissWrap = styled.div<{ $inline?: boolean }>`
  --vui-dismiss-fg: var(--vui-toast-intent-color);
  align-items: flex-start;
  align-self: ${({ $inline }) => ($inline ? 'center' : 'flex-start')};
  display: flex;
  flex-shrink: 0;
`

const ActionBar = styled.div`
  align-items: center;
  background-color: var(--vui-background-default, white);
  box-sizing: border-box;
  display: flex;
  gap: var(--vui-toast-gap-action-bar);
  justify-content: flex-end;
  padding: var(--vui-toast-padding-action-bar);
  width: 100%;
`

const ContentBar = styled.div`
  background-color: var(--vui-toast-content-bg, var(--vui-background-default, white));
  box-sizing: border-box;
  display: flex;
  gap: var(--vui-toast-gap-action-bar);
  padding: var(--vui-toast-padding-action-bar);
  width: 100%;
`

export interface ToastItemProps extends ToastRenderProps {
  onClose?: () => void
}

const resolveToastIcon = (icon: ToastRenderProps['icon'], status: ToastStatus): React.ReactNode => {
  if (icon !== undefined && icon !== '') {
    return typeof icon === 'string' ? <Icon name={icon as any} h={TOAST_ICON_SIZE} w={TOAST_ICON_SIZE} /> : icon
  }

  if (status === 'loading') {
    return <Spinner size="xxs" p={0} />
  }

  return (
    <Icon name={intentIcon[status as ToastIntent] ?? 'uiExclamationCircle'} h={TOAST_ICON_SIZE} w={TOAST_ICON_SIZE} />
  )
}

const ToastItemInner = ({
  action,
  content,
  icon,
  onClose,
  showDismissButton = true,
  size = 'default',
  status = 'info',
  text,
  textLayout = 'stacked',
  title,
}: ToastItemProps) => {
  const isInline = textLayout === 'inline'
  const resolvedIcon = resolveToastIcon(icon, status)

  return (
    <ToastRoot role="status" aria-live="polite" $status={status} $size={size}>
      <Row>
        <IconWrap $inline={isInline}>{resolvedIcon}</IconWrap>
        <TextArea $inline={isInline}>
          {isInline ? (
            <InlineClamp>
              {!!title && <Title $inline>{title}</Title>}
              {!!title && !!text && ' '}
              {!!text && <Description $inline>{text}</Description>}
            </InlineClamp>
          ) : (
            <>
              {!!title && <Title>{title}</Title>}
              {!!text && <Description>{text}</Description>}
            </>
          )}
        </TextArea>
        {showDismissButton && onClose && (
          <DismissWrap $inline={isInline}>
            <DismissButton size="md" onClick={onClose} aria-label="Close" />
          </DismissWrap>
        )}
      </Row>
      {!!content && <ContentBar>{content}</ContentBar>}
      {!!action && <ActionBar>{action}</ActionBar>}
    </ToastRoot>
  )
}

export const ToastItem = memo(ToastItemInner)
ToastItem.displayName = 'ToastItem'
